import { useTranslation } from 'react-i18next';
import {
  CategoryBarChart,
  type CategoryBarDatum,
} from '@/components/common/charts/CategoryBarChart';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Card } from '@/components/ui/card';
import { buildAppointmentStatusTranslationKey } from '@/constants/appointmentOptions';
import { useVisitsByStatusQuery } from '@/hooks/useReportsQueries';
import { resolveSeriesColor } from '@/theme/chartPalette';
import type { ReportsDateRangeValue } from './types';
import { toDateRangeParameters } from './types';

interface VisitsByStatusChartProps {
  dateRange: ReportsDateRangeValue;
}

export function VisitsByStatusChart({ dateRange }: VisitsByStatusChartProps) {
  const { t } = useTranslation(['reports', 'appointments']);
  const query = useVisitsByStatusQuery(toDateRangeParameters(dateRange));
  const rows = query.data === undefined ? [] : query.data;
  const chartData: CategoryBarDatum[] = rows.map(function toDatum(row, index) {
    return {
      key: row.status,
      label: t(buildAppointmentStatusTranslationKey(row.status)),
      value: row.count,
      color: resolveSeriesColor(index),
    };
  });

  return (
    <Card>
      <h2 className="text-sm font-bold text-zooindigo mb-3">
        {t('reports:charts.visitsByStatusTitle')}
      </h2>
      {query.isLoading === true ? (
        <div className="py-10 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : query.isError === true ? (
        <QueryErrorState
          onRetry={function retry() {
            query.refetch();
          }}
        />
      ) : chartData.length === 0 ? (
        <p className="text-xs text-zoomuted italic py-6 text-center">
          {t('reports:charts.emptyState')}
        </p>
      ) : (
        <CategoryBarChart
          data={chartData}
          accessibleLabel={t('reports:charts.visitsByStatusTitle')}
          formatValue={formatWholeNumber}
        />
      )}
    </Card>
  );
}

function formatWholeNumber(value: number): string {
  return String(value);
}
